import { Switch } from "@headlessui/react";

import { Dispatch, JSX, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { UpdateProfileForm } from "@/pages/gate/UpdateProfilePage.tsx";

import { UpdateProfileTab } from "@/components/gate/update-profile/types.ts";

import { useGetProfileDetail } from "@/hooks/api/user.hooks";
import { EditOption, useProfileEditOptions } from "@/hooks/ui/useProfileEditOptions.ts";

interface AdditionalInfoProps {
  setTab: Dispatch<SetStateAction<UpdateProfileTab>>;
}

const AdditionalInfo = ({ setTab }: AdditionalInfoProps): JSX.Element => {
  const { t } = useTranslation();
  const options = useProfileEditOptions();
  const { watch, setValue } = useFormContext<UpdateProfileForm>();
  const params = useParams() as { id: string };
  const { data: profile } = useGetProfileDetail(Number(params.id));

  const pinCode = watch("pin_code");

  const onClick: Record<EditOption, () => void> = {
    [EditOption.PIN_CODE]: () => {
      if (profile.is_lock || !!pinCode) {
        setValue("pin_code", "");
      } else {
        setTab(UpdateProfileTab.PIN_CODE);
      }
    },
    [EditOption.INTERFACE_LANG]: () => setTab(UpdateProfileTab.INTERFACE_LANG),
    [EditOption.CONTENT_LANG]: () => setTab(UpdateProfileTab.CONTENT_LANG),
    [EditOption.RECOMMENDATIONS]: () => setTab(UpdateProfileTab.RECOMMENDATIONS),
  };

  return (
    <div className={"mt-6"}>
      <h2>{t("Profile.additional-settings")}</h2>
      <div className="bg-surface-bright mt-4 flex w-full justify-between gap-3 rounded-2xl p-4">
        <div>
          <h4>{t("Profile.pin-code-set")}</h4>
          <p className="mt-1 max-w-[271px] text-[14px] text-[#919090]">{t("Profile.set-pin-desc")}</p>
        </div>
        <div onClick={() => onClick[EditOption.PIN_CODE]()}>
          <Switch
            checked={profile.is_lock || !!pinCode}
            className="group relative flex h-6 w-10 cursor-pointer rounded-full bg-grey-1 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-orange-1 data-[focus]:outline-1 data-[focus]:outline-white"
          >
            <span
              aria-hidden="true"
              className="group-data-[checked]: pointer-events-none inline-block size-4 translate-x-0 rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
            />
          </Switch>
        </div>
      </div>
      <div className={"mt-3 space-y-3"}>
        {options.map(({ Icon, ...option }) => (
          <button
            key={option.id}
            type={"button"}
            onClick={() => onClick[option.type]()}
            aria-label={option.title}
            className={"bg-surface-bright flex w-full items-center justify-between rounded-2xl px-4 py-3"}
          >
            <span className={"flex gap-3"}>
              <Icon className={"size-6 fill-white"} />
              <span className={"flex flex-col gap-1 text-start"}>
                <span className={"font-lg/6 font-medium max-md:text-base"}>{option.title}</span>
                <span className={"text-on-surface text-sm/6 max-md:text-sm/5"}>{option.description}</span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdditionalInfo;
